import { gql } from '@apollo/client'
import { Button, JusIaIcon, useToast } from '@farol-ds/react'
import {
  DOCVIEW_MODEL_JUSIA_FLAG,
  FEATURE_CLASS,
  FEATURE_NAME,
  SELECTED_OPTION,
  useMetrics,
} from '@jusbrasil-web/docview/core'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  COMPONENT_CLASS,
  GA4FeatureUseEvent,
  GA4ViewItemListEvent,
  REQUIRED_USER_STATUS,
  storeComponentOriginForNextPage,
} from '@jusbrasil-web/shared-ga4-events'
import { useOnScreen } from '@jusbrasil-web/shared-hooks'
import { generateThreadUrl, useJusiaContext } from '@jusbrasil-web/shared-jusia'
import { JUSIA_ALLOWED_ORIGINS } from '@jusbrasil-web/shared-jusia/core'
import { useEffect, useRef } from 'react'

import {
  JusIaPublicationButton_DocumentFragment,
  JusIaPublicationButton_ViewerFragment,
} from './__generated__/jus-ia-publication-button.graphql'

export type JusIaVariant =
  | (typeof DOCVIEW_MODEL_JUSIA_FLAG.VARIANTS)[keyof typeof DOCVIEW_MODEL_JUSIA_FLAG.VARIANTS]
  | ''

const TEMPLATE_IDS = {
  [DOCVIEW_MODEL_JUSIA_FLAG.VARIANTS.MODEL_SUGGESTIONS]: 'MODEL_SUGGESTIONS',
  [DOCVIEW_MODEL_JUSIA_FLAG.VARIANTS.MODEL_USE]: 'MODEL_USE',
} as const

const QUESTIONS = {
  [DOCVIEW_MODEL_JUSIA_FLAG.VARIANTS.MODEL_SUGGESTIONS]: 'Identifique sugestões de modelos',
  [DOCVIEW_MODEL_JUSIA_FLAG.VARIANTS.MODEL_USE]: 'Faça a geração da peça',
} as const

const SELECTED_OPTIONS = {
  [DOCVIEW_MODEL_JUSIA_FLAG.VARIANTS.MODEL_SUGGESTIONS]: SELECTED_OPTION.JUSIA_MODEL_SUGGESTIONS,
  [DOCVIEW_MODEL_JUSIA_FLAG.VARIANTS.MODEL_USE]: SELECTED_OPTION.JUSIA_USE_MODEL,
} as const

const ORIGINS = {
  [DOCVIEW_MODEL_JUSIA_FLAG.VARIANTS.MODEL_SUGGESTIONS]: JUSIA_ALLOWED_ORIGINS.MODEL_SUGGESTIONS,
  [DOCVIEW_MODEL_JUSIA_FLAG.VARIANTS.MODEL_USE]: JUSIA_ALLOWED_ORIGINS.MODEL_USE,
} as const

export interface JusIaPublicationButtonProps {
  document: JusIaPublicationButton_DocumentFragment
  viewer: JusIaPublicationButton_ViewerFragment | null
  ga4ComponentClass?: COMPONENT_CLASS
  documentType: 'model' | 'motion'
  variant: JusIaVariant
  disabled?: boolean
}

export function JusIaPublicationButton({
  document,
  viewer,
  ga4ComponentClass,
  documentType,
  variant,
  disabled = false,
}: JusIaPublicationButtonProps) {
  const { artifact, docId, url, title } = document
  const toast = useToast()
  const { ref: dispatcherRef, sendMetrics } = useMetrics<HTMLDivElement>()
  const { sendGA4Event } = useGA4Dispatcher()
  const { createThreadWithContext, isLoading } = useJusiaContext(viewer?.pid ?? 0)

  const isInViewport = useOnScreen(dispatcherRef, { threshold: 0.7 })
  const hasBeenViewed = useRef(false)

  const templateId = TEMPLATE_IDS[variant as keyof typeof TEMPLATE_IDS]
  const componentClass = ga4ComponentClass || COMPONENT_CLASS.DOCUMENT_HEADER
  const argosNamespace = documentType === 'model' ? 'ModeloPeca' : 'Peca'
  const featureName =
    documentType === 'model' ? FEATURE_NAME.USE_MODEL : FEATURE_NAME.USE_MOTION_MODEL
  // Disable render/viewport metrics for topbar to avoid duplicate events,
  // since topbar is rendered together with header which already sends these events.
  const disableRenderMetrics = componentClass === COMPONENT_CLASS.DOCUMENT_TOPBAR

  useEffect(() => {
    if (disableRenderMetrics || !dispatcherRef.current) return
    sendMetrics(`${argosNamespace}.JusIAButtonRendered`)
    sendGA4Event(
      new GA4ViewItemListEvent({
        renders: [
          {
            item_name: null,
            item_brand: componentClass,
            item_variant: SELECTED_OPTIONS[variant as keyof typeof SELECTED_OPTIONS],
            item_id: null,
          },
        ],
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!disableRenderMetrics && isInViewport && !hasBeenViewed.current) {
      sendMetrics(`${argosNamespace}.JusIAButtonViewed`)
      hasBeenViewed.current = true
    }
  }, [argosNamespace, disableRenderMetrics, isInViewport, sendMetrics])

  if (!viewer || !templateId) return null

  const question = QUESTIONS[variant as keyof typeof QUESTIONS]
  const origin = ORIGINS[variant as keyof typeof ORIGINS]
  const selectedOption = SELECTED_OPTIONS[variant as keyof typeof SELECTED_OPTIONS]

  const onButtonClick = async () => {
    sendMetrics(`${argosNamespace}.JusIAButtonClicked`)
    sendGA4Event(
      new GA4FeatureUseEvent({
        componentClass,
        featureClass: FEATURE_CLASS.AI_LEGAL_SEARCH,
        featureName,
        requiredUserStatus: REQUIRED_USER_STATUS.SUBSCRIBER_USER,
        selectedOption,
      })
    )
    storeComponentOriginForNextPage({
      componentClass,
      componentName: selectedOption,
      selectedOption,
    })

    const context = {
      docId: `${docId}`,
      artifact,
      url: url || '',
      title: title || '',
      court: '',
    }
    try {
      const threadId = await createThreadWithContext({
        context,
        promptId: templateId,
        templateId,
      })
      if (threadId) {
        const threadUrl = generateThreadUrl({
          threadId,
          question,
          autoSend: true,
          origin,
        })
        window.open(threadUrl, '_blank')
      }
    } catch (error) {
      console.error('Failed to create JusIA thread', error)
      toast('Não foi possível explorar com Jus IA. Tente novamente', { type: 'negative' })
    }
  }

  const label = documentType === 'model' ? 'Usar modelo' : 'Usar peça'

  return (
    <div ref={dispatcherRef}>
      <Button
        kind="secondary"
        leftIcon={<JusIaIcon />}
        onClick={onButtonClick}
        loading={isLoading}
        disabled={disabled}
      >
        {label}
      </Button>
    </div>
  )
}

JusIaPublicationButton.fragments = {
  document: gql`
    fragment JusIaPublicationButton_document on Document {
      docId
      artifact
      url
      title
    }
  `,
  viewer: gql`
    fragment JusIaPublicationButton_viewer on Profile {
      pid
    }
  `,
}
